import React, { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getOverrides, useClasses } from '../../../utils/overrides';
import { createUseStyles, useTheme } from '../../../utils/styles';
import Icon from '../../general/Icon';
import Text from '../../typography/Text';
import Popover from '../Popover';
import styles from './styles';

const useStyles = createUseStyles(styles, 'SelectChipStates');

const getOptionAppearance = (option, theme) => {
    const defaultTextColor = theme.colors.actionMinor[600];
    const defaultBgColor = theme.colors.background.utility025;

    return {
        bgColor: option?.bgColor || defaultBgColor,
        hoverBgColor: option?.hoverBgColor || option?.bgColor || theme.colors.actionMinor[150],
        borderColor: option?.borderColor || theme.colors.actionMinor[200],
        openBorderColor: option?.openBorderColor || option?.textColor || defaultTextColor,
        textColor: option?.textColor || defaultTextColor,
        iconColor: option?.iconColor || option?.textColor || defaultTextColor,
        iconBgColor: option?.iconBgColor || option?.bgColor || defaultBgColor,
    };
};

const SelectChipStates = memo(
    ({
        classes: classesProp,
        overrides: overridesProp,
        className: classNameProp,
        options,
        value,
        onChange,
        title,
        isDisabled,
        placement = 'bottomLeft',
        getIsOpen,
    }) => {
        const theme = useTheme();
        const classes = useClasses(useStyles, classesProp);
        const override = getOverrides(overridesProp, SelectChipStates.overrides);
        const [isOpen, setIsOpen] = useState(false);
        const [isChipHovered, setIsChipHovered] = useState(false);
        const [hoveredValue, setHoveredValue] = useState(null);

        const selectedOption = useMemo(
            () => (options || []).find((o) => o.value === value),
            [options, value],
        );

        const selectedAppearance = useMemo(
            () => getOptionAppearance(selectedOption, theme),
            [selectedOption, theme],
        );

        const chipStyle = useMemo(
            () => ({
                '--select-chip-bg-color': selectedAppearance.bgColor,
                '--select-chip-hover-bg-color': selectedAppearance.hoverBgColor,
                '--select-chip-border-color': selectedAppearance.borderColor,
                '--select-chip-open-border-color': selectedAppearance.openBorderColor,
            }),
            [selectedAppearance],
        );

        const handleVisibleChange = useCallback(
            (open) => {
                if (isDisabled) return;
                setIsOpen(open);
                getIsOpen && getIsOpen(open);
            },
            [isDisabled, getIsOpen],
        );

        const handleOptionClick = useCallback(
            (opt) => {
                if (opt.isDisabled) return;
                onChange && onChange(opt);
                setIsOpen(false);
                getIsOpen && getIsOpen(false);
            },
            [onChange, getIsOpen],
        );

        const chipClassName = classnames(
            classes.chip,
            classes.chipSurface,
            { [classes.chipDisabled]: isDisabled },
            { [classes.chipHovered]: isChipHovered && !isOpen && !isDisabled },
            { [classes.chipOpen]: isOpen },
            classNameProp,
        );

        const chipOverrides = override.chip || {};
        const chipTextOverrides = override.chipText || {};
        const chipDropdownIconOverrides = override.chipDropdownIcon || {};
        const dropdownRootOverrides = override.dropdownRoot || {};
        const dropdownTitleOverrides = override.dropdownTitle || {};
        const optionOverrides = override.option || {};
        const optionAvatarOverrides = override.optionAvatar || {};
        const optionAvatarIconOverrides = override.optionAvatarIcon || {};
        const optionTextOverrides = override.optionText || {};
        const optionCheckIconOverrides = override.optionCheckIcon || {};

        const chipContent = (
            <div
                className={chipClassName}
                onMouseEnter={!isDisabled ? () => setIsChipHovered(true) : undefined}
                onMouseLeave={() => setIsChipHovered(false)}
                {...chipOverrides}
                style={{ ...chipStyle, ...chipOverrides.style }}
            >
                <Text
                    type="badges"
                    className={classes.chipText}
                    color={selectedAppearance.textColor}
                    {...chipTextOverrides}
                >
                    {selectedOption?.label || ''}
                </Text>
                <span
                    className={classnames(classes.chipIcon, { [classes.chipIconOpen]: isOpen })}
                    {...(override.chipIcon || {})}
                >
                    <Icon
                        name="arrowDownRaw"
                        color={selectedAppearance.iconColor}
                        size="small"
                        {...chipDropdownIconOverrides}
                    />
                </span>
            </div>
        );

        const dropdownContent = (
            <div className={classes.dropdownRoot} {...dropdownRootOverrides}>
                {title && (
                    <div className={classes.dropdownTitle} {...dropdownTitleOverrides}>
                        {title}
                    </div>
                )}
                {(options || []).map((opt) => {
                    const isActive = opt.value === value;
                    const isHovered = hoveredValue === opt.value && !opt.isDisabled && !isActive;
                    const optionAppearance = getOptionAppearance(opt, theme);
                    const optionClassName = classnames(classes.option, {
                        [classes.optionSelected]: isActive,
                        [classes.optionHovered]: isHovered,
                        [classes.optionDisabled]: opt.isDisabled,
                    });

                    const avatarBgColor = isActive
                        ? optionAppearance.iconBgColor
                        : theme.colors.background.utility025;
                    const avatarIconColor = isActive
                        ? optionAppearance.iconColor
                        : theme.colors.actionMinor[400];

                    return (
                        <div
                            key={opt.value}
                            className={optionClassName}
                            onClick={!opt.isDisabled ? () => handleOptionClick(opt) : undefined}
                            onMouseEnter={() => !opt.isDisabled && setHoveredValue(opt.value)}
                            onMouseLeave={() => setHoveredValue(null)}
                            {...optionOverrides}
                        >
                            {opt.iconType && (
                                <div
                                    className={classes.optionAvatar}
                                    {...optionAvatarOverrides}
                                    style={{
                                        backgroundColor: avatarBgColor,
                                        ...optionAvatarOverrides.style,
                                    }}
                                >
                                    <Icon
                                        className={classes.optionAvatarIcon}
                                        name={opt.iconType}
                                        color={avatarIconColor}
                                        {...optionAvatarIconOverrides}
                                    />
                                </div>
                            )}
                            <Text
                                type="body"
                                className={classes.optionText}
                                medium={isActive}
                                {...optionTextOverrides}
                            >
                                {opt.label}
                            </Text>
                            {isActive && (
                                <Icon
                                    className={classes.optionCheckIcon}
                                    name="check"
                                    color={theme.colors.actionMajor[500]}
                                    {...optionCheckIconOverrides}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        );

        return (
            <div className={classes.root} {...(override.root || {})}>
                <Popover
                    content={dropdownContent}
                    placement={placement}
                    trigger={['click']}
                    visible={isOpen}
                    onVisibleChange={handleVisibleChange}
                    className={classes.Popover}
                    {...(override.Popover || {})}
                >
                    {chipContent}
                </Popover>
            </div>
        );
    },
);

SelectChipStates.displayName = 'SelectChipStates';

SelectChipStates.overrides = [
    'root',
    'chip',
    'chipText',
    'chipIcon',
    'chipDropdownIcon',
    'dropdownRoot',
    'dropdownTitle',
    'option',
    'optionAvatar',
    'optionAvatarIcon',
    'optionText',
    'optionCheckIcon',
    'Popover',
];

SelectChipStates.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.any.isRequired,
            bgColor: PropTypes.string,
            hoverBgColor: PropTypes.string,
            borderColor: PropTypes.string,
            openBorderColor: PropTypes.string,
            textColor: PropTypes.string,
            iconColor: PropTypes.string,
            iconBgColor: PropTypes.string,
            iconType: PropTypes.string,
            isDisabled: PropTypes.bool,
        }),
    ),
    value: PropTypes.any,
    onChange: PropTypes.func,
    title: PropTypes.string,
    isDisabled: PropTypes.bool,
    placement: PropTypes.string,
    getIsOpen: PropTypes.func,
    classes: PropTypes.object,
    overrides: PropTypes.object,
    className: PropTypes.string,
};

export default SelectChipStates;
